/* eslint-disable import/no-import-module-exports */
import 'core-js/modules/es.promise';
import 'core-js/modules/es.array.iterator';
import './i18n';
import 'dayjs/locale/se';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { get } from 'lodash';
import {
  init as initSentry,
  reactRouterV6BrowserTracingIntegration as sentryBrowserTracingIntegration,
  setTag as setSentryTag,
} from '@sentry/react';

import { LicenseInfo } from '@mui/x-license';

import App from './App';

import '../types/global.d';

const MUI_LICENSE_KEY =
  '8926ba31682661914fbfa6fc6b63e6eeTz05NjE4MSxFPTE3NTUyNzY2NjYwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLFBWPWluaXRpYWwsS1Y9Mg==';
LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(objectSupport);

if (window.fromServer.env.SENTRY_STAGE !== 'local') {
  initSentry({
    dsn: window.fromServer.env.SENTRY_DSN,
    environment: window.fromServer.env.SENTRY_STAGE,
    release: `YED_${window.fromServer.env.SENTRY_STAGE}`,
    integrations: [
      sentryBrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 0.2,
    beforeSend(event, hint) {
      const error = hint.originalException;

      if (get(error, 'status') === 401) {
        return null;
      }

      return event;
    },
  });

  setSentryTag('service', 'console');
}

const container = document.getElementById('app');
const root = createRoot(container);

if (module.hot) {
  module.hot.accept('./App', () => {
    root.render(<App />);
  });
}

// Enables MSW to mock requests sent from the user's browser
async function enableMocking() {
  if (process.env.NODE_ENV === 'development') {
    const { worker } = await import('./browser');

    // `worker.start()` returns a Promise that resolves
    // once the Service Worker is up and ready to intercept requests.
    await worker.start({ onUnhandledRequest: 'bypass' });
  }
}

// This would enable request mocking
enableMocking().then(() => {
  root.render(<App />);
});
